/**
 * G8 / V28 voice mode contracts: the research library entity (`research`, sealed per project or
 * per user), the `viko_research_save` MCP tool the agent calls while researching in a
 * conversation, and the distillation proposal cards the device builds from a conversation's
 * outcomes for review. Everything here lives inside E2E-encrypted entities or on-device; the
 * hub never sees titles, summaries, URLs or conversation text.
 *
 * Shared by the PWA and (later) the native iOS/watchOS clients, so shapes stay small and flat.
 */
import { z } from 'zod';
import { PrivateEntityId } from './encrypted-entities.js';
const Id = PrivateEntityId;
const At = z.number().int().nonnegative();
const LocalId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
export const RESEARCH_SOURCES_MAX = 30;
export const RESEARCH_SUMMARY_MAX = 8000;
/** http(s) only; embedded credentials are stripped. Null when the URL is not a web page. */
export function sanitizeResearchUrl(raw) {
    if (raw.length > 2000)
        return null;
    let url;
    try {
        url = new URL(raw.trim());
    }
    catch {
        return null;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:')
        return null;
    if (!url.hostname)
        return null;
    url.username = '';
    url.password = '';
    return url.toString();
}
const WebUrl = z
    .string()
    .max(2000)
    .refine((u) => {
    const clean = sanitizeResearchUrl(u);
    // Stored URLs are already clean: a credentialed or non-web URL is refused, not rewritten.
    return clean !== null && !/^[a-z]+:\/\/[^/]*@/i.test(u.trim());
}, 'source must be an http(s) URL without credentials');
export const ResearchSource = z.strictObject({
    url: WebUrl,
    title: z.string().trim().min(1).max(300),
    /** What this source contributed (one or two sentences). */
    note: z.string().max(1000).optional(),
});
/** Entity value for kind `research` (the id lives in the authenticated entity context). */
export const ResearchRecord = z
    .strictObject({
    v: z.literal(1),
    scope: z.enum(['project', 'user']),
    projectId: Id.optional(),
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().min(1).max(RESEARCH_SUMMARY_MAX),
    sources: z.array(ResearchSource).max(RESEARCH_SOURCES_MAX),
    /** The question that was researched, when it differs from the title. */
    query: z.string().max(1000).optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(10),
    by: z.enum(['agent', 'user']),
    /** The conversation it came from (drillable back to the item and the run). */
    conversation: z
        .strictObject({
        barkId: Id.optional(),
        runId: z.string().min(1).max(256).optional(),
        messageId: LocalId.optional(),
    })
        .optional(),
    createdAt: At,
    updatedAt: At,
})
    .refine((r) => (r.scope === 'project' ? !!r.projectId : r.projectId === undefined), 'project research needs a project; user research has none');
/** FNV-1a (not a security primitive): a compact, stable local idempotency key. */
function fnv1a(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
/** A repeated save of the same finding in the same run resolves to the same entity. */
export function researchIntent(runId, title, urls) {
    const key = JSON.stringify([title.trim().toLowerCase(), [...urls].sort()]);
    return `research:${runId}:${fnv1a(key)}:${key.length}`;
}
// --- MCP tool (available to every run role; always scoped to the run's own project) -----------
export const VikoResearchSaveInput = z.object({
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().min(1).max(RESEARCH_SUMMARY_MAX),
    sources: z
        .array(z.object({
        url: z.string().max(2000),
        title: z.string().trim().min(1).max(300),
        note: z.string().max(1000).optional(),
    }))
        .min(1)
        .max(RESEARCH_SOURCES_MAX),
    query: z.string().max(1000).optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
    /** `user`: useful beyond this project (kept in the user's library, not the project's). */
    scope: z.enum(['project', 'user']).optional(),
    /** The conversation item this research belongs to (triage discuss sessions). */
    barkId: Id.optional(),
});
export const VikoResearchSaveOutput = z.object({
    researchId: Id,
    scope: z.enum(['project', 'user']),
    /** Sources dropped as unsafe or not web pages. */
    dropped: z.number().int().nonnegative(),
});
export const VOICE_MCP_TOOLS = {
    viko_research_save: { input: VikoResearchSaveInput, output: VikoResearchSaveOutput },
};
// --- distillation (device-built from the sealed conversation; reviewed before anything runs) ---
export const DistillChange = z.discriminatedUnion('op', [
    z.strictObject({ op: z.literal('goal.set'), goal: z.string().trim().min(1).max(4000) }),
    z.strictObject({
        op: z.literal('plan.brief'),
        /** Null: a new workstream named `name` carries the plan. */
        workstreamId: Id.nullable(),
        name: z.string().trim().min(1).max(200),
        brief: z.string().trim().min(1).max(20_000),
    }),
    z.strictObject({
        op: z.literal('task.create'),
        title: z.string().trim().min(1).max(200),
        description: z.string().max(20_000).optional(),
        acceptance: z.array(z.string().max(4000)).max(20).optional(),
        workstreamId: Id.optional(),
    }),
    z.strictObject({
        op: z.literal('task.priority'),
        taskId: Id,
        priority: z.number().int().min(0).max(10),
    }),
    /** An open agent proposal in the conversation: accepting confirms it through its signed digest. */
    z.strictObject({
        op: z.literal('bark.confirm'),
        proposalId: LocalId,
        actions: z.number().int().positive().max(20),
    }),
]);
export const DistillProposal = z.strictObject({
    id: LocalId,
    projectId: Id,
    barkId: Id,
    title: z.string().min(1).max(200),
    change: DistillChange,
    /** What was said that led here (message ids + short quotes). Never empty. */
    evidence: z
        .array(z.strictObject({ messageId: LocalId, quote: z.string().max(500) }))
        .min(1)
        .max(10),
    /** Research entries cited by the conversation. */
    research: z.array(Id).max(10).optional(),
    status: z.enum(['open', 'accepted', 'dismissed']),
});
//# sourceMappingURL=voice.js.map